import { FilterType } from '@monetize/types/common';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { MdOutlineFileDownload, MdRefresh } from 'react-icons/md';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  MBox,
  MCustomIconButton,
  MFlex,
  MPageContainer,
  MSettingsPageHeader,
  MText,
  MTooltip,
} from '../../../../components/Monetize';
import { useTableColumns } from '../../../../components/Monetize/DataTableNew/columnCatalog/useTableColumns';
import { DataTable } from '../../../../components/Monetize/DataTableNew/DataTable';
import { LoadMore } from '../../../../components/Monetize/LoadMore';
import { useCrmRecordLinkConfig } from '../../../../components/IntegrationLog';
import { getIntegrationLogViewRoute } from '../../../../constants/routes';
import { useIntegrationLogCsvExport } from '../../../../hooks/useIntegrationLogCsvExport';
import { useIntegrationLogData } from '../../../../hooks/useIntegrationLogData';
import { useDocumentHead } from '../../../../services/documentHead';
import {
  IntegrationLogColumnContext,
  INTEGRATION_LOG_CATALOG,
} from './integrationLog.columns';

export function IntegrationLog() {
  const { setDocTitle } = useDocumentHead();
  const [fetchCsvCount, setFetchCsvCount] = useState(1);
  const [isManualRefresh, setIsManualRefresh] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const {
    filters,
    filterParams,
    logData,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    isError,
    setFilters,
    fetchNextPage,
    refetch,
  } = useIntegrationLogData();
  const { downloadCsv, isDownloading } =
    useIntegrationLogCsvExport(filterParams);

  const hasFiltersApplied = filters.length > 0;

  const linkConfig = useCrmRecordLinkConfig();
  const columnContext = useMemo<IntegrationLogColumnContext>(
    () => ({ linkConfig }),
    [linkConfig],
  );
  const { columns, state: columnState } = useTableColumns(
    INTEGRATION_LOG_CATALOG,
    columnContext,
  );

  const handleFiltersChange = useCallback(
    (nextFilters: FilterType[]) => {
      setFilters(nextFilters);
      // CSV export re-fetches as many pages as the list has loaded, and
      // filtering drops the list back to the first page.
      setFetchCsvCount(1);
    },
    [setFilters],
  );

  const handleLoadMore = () => {
    setFetchCsvCount((prev) => prev + 1);
    fetchNextPage();
  };

  const handleDownload = () => {
    const headers = [
      {
        label: 'Timestamp',
        value: 'lastUpdatedAt',
      },
      { label: 'Object', value: 'entityName' },
      { label: 'Operation', value: 'eventType' },
      { label: 'Label', value: 'label' },
      { label: 'Source Record ID', value: 'sourceId' },
      { label: 'Target Record ID', value: 'targetId' },
      { label: 'Status', value: 'status' },
    ];

    downloadCsv(fetchCsvCount, headers, 'integration_log_export.csv');
    // when download is done reset the fetchCsv count to 1
    setFetchCsvCount(1);
  };

  // Open the detail modal via a nested route so it is directly shareable /
  // deep-linkable. Preserve any active filters so closing returns to the same
  // filtered list.
  const onRowClick = (logId: number) => {
    navigate(`${getIntegrationLogViewRoute(String(logId))}${location.search}`);
  };

  useEffect(() => {
    setDocTitle('Settings', 'Integration Logs');
  }, [setDocTitle]);

  useEffect(() => {
    if (!isFetching && isManualRefresh) {
      setIsManualRefresh(false);
    }
  }, [isFetching, isManualRefresh]);

  return (
    <MPageContainer
      alignItems="stretch"
      overflow="hidden"
      h="calc(100vh - 80px)"
      minH="0"
    >
      <MSettingsPageHeader title="Integration Logs">
        {(!isFetching || isManualRefresh) && (
          <>
            <MTooltip label="Refresh Data" placement="bottom-end">
              <MCustomIconButton
                variant="icon"
                containerSize="8"
                btnSize={6}
                fontSize="lg"
                cursor="pointer"
                icon={MdRefresh}
                aria-label="Refresh Data"
                onClick={() => {
                  setIsManualRefresh(true);
                  refetch();
                }}
                isLoading={isManualRefresh}
              />
            </MTooltip>
            <MTooltip label="Export CSV" placement="bottom-end">
              <MCustomIconButton
                variant="icon"
                containerSize="8"
                btnSize={6}
                fontSize="lg"
                cursor="pointer"
                icon={MdOutlineFileDownload}
                aria-label="Export CSV"
                onClick={handleDownload}
                isLoading={isDownloading}
                isDisabled={logData.length === 0}
              />
            </MTooltip>
          </>
        )}
      </MSettingsPageHeader>
      <MFlex justifyContent="space-between" mb={8}>
        <MText>
          Status of record synchronization between MonetizeNow and External
          Integrations
        </MText>
      </MFlex>
      <MBox
        flex="1"
        minH="0"
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        <DataTable
          columns={columns}
          state={columnState}
          data={logData}
          isLoading={isFetching && !isFetchingNextPage && !isManualRefresh}
          enablePagination={false}
          skipFilterValuePreprocessing
          initialFilters={filters}
          onFiltersChange={handleFiltersChange}
          onRowClick={(row) => onRowClick(row.id)}
          singleEmptyPlaceholder
          emptyFilterResultsProps={{
            mainMessage: 'Looks like there are no logs here.',
            smallMessage: null,
            btnLabel: 'Check again',
            onReload: () => refetch(),
            secondaryBtnLabel: hasFiltersApplied ? 'Clear filters' : undefined,
          }}
          resetFilter={() => setFilters([])}
          containerProps={{ maxHeight: 'none', flex: '1', minH: '0', pb: 0 }}
        />
        {!isError && logData.length !== 0 && (
          <MBox pb={4} flexShrink={0}>
            <LoadMore
              fetchedElementLength={logData.length}
              isLoading={isFetchingNextPage}
              hasMoreItem={hasNextPage}
              onLoadMore={handleLoadMore}
            />
          </MBox>
        )}
      </MBox>

      {/* Nested :id/view route renders the detail modal on top of the list */}
      <Outlet />
    </MPageContainer>
  );
}
